import { useState, useEffect } from "react";
import { ChevronDown, ExternalLink, Ship, ArrowRight, Clock, Search, Zap } from "lucide-react";
import { OOCL_SERVICES, OOCL_INTRO, OOCL_SOURCE_URL } from "@/lib/ooclServices";

const REGION_STYLE = {
  "Australia": "bg-blue-50 text-blue-700",
  "New Zealand": "bg-violet-50 text-violet-700",
  "Australia & New Zealand": "bg-teal-50 text-teal-700",
  "Trans-Tasman": "bg-amber-50 text-amber-700",
};

function Rotation({ label, ports }) {
  if (!ports?.length) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-num mb-1.5">{label}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {ports.map((p, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <span className="rounded-md bg-slate-100 text-slate-700 text-xs px-2 py-1 whitespace-nowrap">{p}</span>
            {i < ports.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
          </span>
        ))}
      </div>
    </div>
  );
}

function TransitTable({ group }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 text-xs font-medium text-slate-700">{group.title}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-100 font-num">
          {group.rows.map((r, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="px-3 py-1.5 text-slate-600">{r[0]}</td>
              <td className="px-2 py-1.5 text-slate-300"><ArrowRight className="w-3 h-3" /></td>
              <td className="px-3 py-1.5 text-slate-700">{r[1]}</td>
              <td className="px-3 py-1.5 text-right font-semibold text-slate-900 whitespace-nowrap">{r[2]} days</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ServiceCard({ svc, forceOpen }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`service-card-${svc.code}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`service-toggle-${svc.code}`}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors">
        <span className="inline-flex items-center justify-center rounded-lg bg-primary/10 text-primary font-display font-bold text-sm px-2.5 py-1.5 min-w-[52px]">
          {svc.code}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-slate-900 text-sm truncate">{svc.name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 font-num">
            {svc.freq} · {svc.round}{svc.origins?.length ? ` · from ${svc.origins.slice(0, 4).join(", ")}${svc.origins.length > 4 ? "\u2026" : ""}` : ""}
          </div>
        </div>
        <span className={`hidden sm:inline text-[11px] font-medium rounded-full px-2.5 py-1 ${REGION_STYLE[svc.region] || "bg-slate-100 text-slate-600"}`}>
          {svc.region}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-5" data-testid={`service-detail-${svc.code}`}>
          {svc.note && (
            <p className="text-sm text-slate-600 leading-relaxed pt-3">{svc.note}</p>
          )}
          <div className="grid gap-4">
            <Rotation label="Southbound / inbound rotation" ports={svc.sb} />
            <Rotation label="Northbound / outbound rotation" ports={svc.nb} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {svc.transits.map((g, i) => <TransitTable key={i} group={g} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export function ServicesPanel() {
  const [q, setQ] = useState("");
  const query = q.trim();
  const norm = (s) => s.toLowerCase().trim();
  const parts = query.split(/→|->|\bto\b/i).map(norm).filter(Boolean);
  const isLane = parts.length >= 2;
  const [oTerm, dTerm] = parts;

  const matchService = (svc) => {
    if (!query) return true;
    if (isLane) {
      return svc.transits.some((g) => g.rows.some((r) => norm(r[0]).includes(oTerm) && norm(r[1]).includes(dTerm)));
    }
    const hay = [svc.code, svc.name, ...(svc.nb || []), ...(svc.sb || []), ...(svc.origins || [])].join(" ").toLowerCase();
    return hay.includes(norm(query));
  };
  const filtered = OOCL_SERVICES.filter(matchService);

  const laneMatches = isLane
    ? OOCL_SERVICES.flatMap((svc) =>
        svc.transits.flatMap((g) =>
          g.rows
            .filter((r) => norm(r[0]).includes(oTerm) && norm(r[1]).includes(dTerm))
            .map((r) => ({ code: svc.code, name: svc.name, from: r[0], to: r[1], days: r[2] }))
        )
      ).sort((a, b) => a.days - b.days)
    : [];

  return (
    <div className="space-y-6" data-testid="services-panel">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Carrier services</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Reference directory of carrier service loops calling Australia &amp; New Zealand — port rotations, sailing frequency and
          port-to-port transit times. More carriers can be added over time.
        </p>
      </div>

      {/* Find a service / route search */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="services-search"
          placeholder='Find a service or route — e.g. "Laem Chabang → Sydney", "Fremantle" or "AAA1"'
          className="w-full rounded-lg border border-slate-200 bg-white pl-10 pr-10 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="services-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">Clear</button>
        )}
      </div>

      {isLane && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="lane-results">
          <div className="flex items-center gap-2 text-primary mb-2">
            <Zap className="w-4 h-4" />
            <span className="text-sm font-display font-semibold">
              {laneMatches.length ? `${laneMatches.length} route${laneMatches.length === 1 ? "" : "s"} found` : "No direct OOCL route matches that origin → destination"}
            </span>
          </div>
          {laneMatches.length > 0 && (
            <ul className="space-y-1.5">
              {laneMatches.map((m, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`lane-match-${i}`}>
                  <span className="inline-flex items-center justify-center rounded bg-primary/10 text-primary font-display font-bold text-xs px-2 py-0.5">{m.code}</span>
                  <span className="text-slate-700 font-num">{m.from} <ArrowRight className="w-3 h-3 inline text-slate-400" /> {m.to}</span>
                  <span className="font-semibold text-slate-900 font-num">{m.days} days</span>
                  {i === 0 && laneMatches.length > 1 && <span className="text-[10px] uppercase tracking-wider font-num text-emerald-600 font-semibold">fastest</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* OOCL carrier block */}
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="carrier-oocl">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Ship className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-semibold text-slate-900">OOCL</h3>
            <p className="text-[11px] text-slate-500 font-num">
              {query ? `${filtered.length} of ${OOCL_SERVICES.length} services match` : `${OOCL_SERVICES.length} Australia / New Zealand services`}
            </p>
          </div>
          <a href={OOCL_SOURCE_URL} target="_blank" rel="noreferrer" data-testid="oocl-source-link"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
            OOCL service routes <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-4xl">{OOCL_INTRO}</p>
        <p className="text-[11px] text-slate-400 mt-2 font-num flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Transit times &amp; rotations from the OOCL Australia/New Zealand Product &amp; Service Guide 2024 — indicative, confirm live schedules at booking.
        </p>
      </div>

      <div className="space-y-3">
        {filtered.map((svc) => <ServiceCard key={svc.code} svc={svc} forceOpen={!!query} />)}
        {!filtered.length && (
          <div className="text-center text-sm text-slate-400 py-10" data-testid="services-empty">No services match "{q}".</div>
        )}
      </div>
    </div>
  );
}
